import React, { InputHTMLAttributes } from 'react'
import cn from 'classnames'
import styled, { css } from 'styled-components'
import { DcDiv } from '../dc-div/dc-div'
import { DcText } from '../dc-text/dc-text'
import { $secondaryWithAlpha, colors } from '../styleGuide'

import DCDatepicker from './components/DCDatepicker'
import Input from './components/Input'
import OtpInput from './components/OtpInput'
import PasswordInput from './components/passwordInput'
import TextareaInput from './components/TextareaInput'
import TypeAHead from './components/TypeAHead'

export interface DcProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string
  inputClassName?: string
  label?: string
  name: string
  error?: string
  type?: string
  shadow?: boolean
  as?: string
  isTextarea?: boolean
  variant?: 'normal' | 'solid' | 'outline' | 'line'
  dimension?: 'small' | 'medium' | 'big'
  onChange?: any
  value?: any
  parentStyle?: any
  isMakingInput?: boolean
  startDate?: any
  endDate?: any
  labelKey?: any
  noPadding?: boolean
  children?: any
  invalid?: boolean
  showsearchicon?: any
  placeholder?: string
  isShowLabel?: boolean
  customLabel?: string
  isClearOnSelection?: boolean
  inputSize?: 'small' | 'medium' | 'large'
  onSelectedOption?: () => {}
  isShowError?: boolean
  errorMessage?: string
  // OTP specific props
  otpLength?: number
  onOtpComplete?: (value: string) => void
  otpGap?: string
  otpInputWidth?: string
  otpInputHeight?: string
  // Icon props
  isShowIcon?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  iconClick?: () => void
}

const variantClasses = {
  normal:
    'bg-gray-100 border border-border-base rounded focus:shadow focus:bg-light focus:border-accent',
  solid:
    'bg-gray-100 border border-border-100 rounded focus:bg-light focus:border-accent',
  outline: 'border border-border-base rounded focus:border-accent',
  line: 'ps-0 border-b border-border-base rounded-none focus:border-accent',
}

const PixelInputContainer = styled.div<{ as: string; width: string }>`
  position: relative;
  width: ${(props) => props.width || '100%'};
  &.overFlowCustom {
    overflow: visible !important;
    & > * {
      overflow: visible !important;
      & > * {
        input {
          ${(props: DcProps) =>
            props.invalid === true &&
            css`
              color: red !important;
            `}
        }
      }
    }
  }
  & > * {
    overflow: visible !important;
  }
  textarea {
    resize: none;
    height: 150px;
    min-width: 100%;
    outline: none;
    &::placeholder {
      font-size: 16px;
      padding: 2px;
    }
  }
  .sc-jSMfEi {
    min-height: 38px !important;
    border-radius: 0.25rem !important;
    overflow: hidden;
    &:focus {
      box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')} !important;
    }
    &:focus-within {
      box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')} !important;
    }
  }
  .eoOphk {
    &:focus {
      background: ${colors.blue};
    }
  }
  .fdjKYC {
    min-height: 38px !important;
    border-radius: 0.25rem !important;
    overflow: hidden;
    &:focus {
      box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')} !important;
    }
  }
  .kvwrSX {
    min-height: 38px !important;
    border-radius: 0.25rem !important;
    overflow: hidden;
    box-shadow: 0 2px 4px ${colors.grayShadow};
  }
  label {
    border-radius: 4px !important;
    border: 1px solid ${colors.grayInputBorder} !important;
    & + div {
      z-index: 99999;
    }
  }
  .covbmQ {
    top: 13px !important;
  }
  background-color: ${colors.white};
`

const InputError = styled.span`
  font-size: 90%;
  color: ${colors.black};
`
const sizeClasses = {
  small: 'text-sm h-10',
  medium: 'h-12',
  big: 'h-14',
}

export const DcInput = React.forwardRef<any, DcProps>(
  (
    {
      className,
      label,
      name,
      error,
      children,
      variant = 'normal',
      dimension = 'big',
      shadow = false,
      disabled = false,
      isTextarea = false,
      as = 'text',
      type = 'text',
      onChange,
      value,
      inputClassName,
      isMakingInput = false,
      parentStyle = {},
      startDate,
      endDate,
      labelKey,
      noPadding = false,
      invalid = false,
      showsearchicon = 1,
      placeholder = '',
      isShowLabel = true,
      customLabel = '',
      isClearOnSelection = false,
      onSelectedOption = () => {},
      inputSize,
      isShowError = false,
      errorMessage = '',
      // OTP specific props
      otpLength = 6,
      onOtpComplete,
      otpGap = '10px',
      otpInputWidth = '50px',
      otpInputHeight = '50px',
      // Icon props
      isShowIcon = false,
      icon,
      iconPosition = 'right',
      iconClick,
      width,
      ...rest
    },
    ref,
  ) => {
    const [showDatePicker, setShowDatePicker] = React.useState<any>(
      as === 'dateRange' ? null : false,
    )

    // Ensure value is always defined to prevent controlled/uncontrolled switching
    const normalizedValue = value ?? ''

    if (as === 'textarea') {
      rest['as'] = as
    }
    return label ? (
      <DcDiv
        width="100%"
        height="auto"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flexStart',
        }}
      >
        <DcText
          style={{
            textAlign: 'left',
            color: colors.grayTextDark,
            fontSize: '.875rem',
            fontWeight: '500',
            marginBottom: '.5rem',
          }}
        >
          {label}
        </DcText>
        <PixelInputContainer
          invalid={invalid}
          className={'overFlowCustom'}
          style={parentStyle}
          width={width}
        >
          <React.Fragment>
            {as === 'datePicker' ? (
              <React.Fragment>
                <DCDatepicker
                  ref={ref}
                  value={normalizedValue}
                  className={cn(
                    ' px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                    shadow && 'focus:shadow',
                    variantClasses[variant],
                    sizeClasses[dimension],
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    inputClassName,
                  )}
                  disabled={disabled}
                  aria-invalid={error ? 'true' : 'false'}
                  rest={rest}
                  showResetDate={false}
                  showClose={false}
                  label={label}
                  placeholder={placeholder}
                  inputId={name}
                  onChange={onChange}
                  onDateChange={(data) =>
                    onChange(
                      { target: { value: data.date } },
                      setShowDatePicker(false),
                    )
                  }
                  onFocusChange={(focusedInput) =>
                    setShowDatePicker(focusedInput)
                  }
                  showDatepicker={showDatePicker}
                />
              </React.Fragment>
            ) : as === 'typeahead' ? (
              <TypeAHead
                onSelectedOption={onSelectedOption}
                isClearOnSelection={isClearOnSelection}
                customLabel={customLabel}
                isShowLabel={isShowLabel}
                placeholder={placeholder}
                id={name}
                name={name}
                type={type}
                ref={ref}
                labelKey={labelKey}
                className={cn(
                  'flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                  shadow && 'focus:shadow',
                  variantClasses[variant],
                  sizeClasses[dimension],
                  disabled && 'bg-gray-100 cursor-not-allowed',
                  inputClassName,
                )}
                disabled={disabled}
                value={normalizedValue}
                spellCheck="false"
                onChange={onChange}
                invalid={invalid}
                aria-invalid={error ? 'true' : 'false'}
                isShowIcon={isShowIcon}
                icon={icon}
                iconPosition={iconPosition}
                iconClick={iconClick}
                {...rest}
              />
            ) : as === 'password' ? (
              <PasswordInput
                value={normalizedValue}
                onChange={onChange}
                placeholder={placeholder}
                id={name}
                name={name}
                type={type}
                ref={ref}
                spellCheck="false"
                aria-invalid={error ? 'true' : 'false'}
                rest={rest}
                showsearchicon={showsearchicon}
                className={className}
                disabled={disabled}
              />
            ) : as === 'textarea' ? (
              <TextareaInput
                value={normalizedValue}
                onChange={onChange}
                placeholder={placeholder}
                id={name}
                name={name}
                ref={ref}
                variant={variant}
                dimension={dimension}
                disabled={disabled}
                error={error}
                isShowError={isShowError}
                errorMessage={errorMessage}
              />
            ) : as === 'otp' ? (
              <OtpInput
                length={otpLength}
                value={normalizedValue}
                onChange={onChange}
                onComplete={onOtpComplete}
                placeholder={placeholder}
                className={inputClassName}
                disabled={disabled}
                autoFocus={rest.autoFocus}
                size={inputSize}
                variant={variant}
                color={rest.color || 'primary'}
                isShowError={isShowError}
                errorMessage={errorMessage}
                label={label}
                isShowLabel={isShowLabel}
                isRequired={rest.required}
                helperText={rest.helperText}
                isShowHelperText={rest.isShowHelperText}
                isShowErrorMessage={rest.isShowErrorMessage}
                fullWidth={rest.fullWidth}
                gap={otpGap}
                inputWidth={otpInputWidth}
                inputHeight={otpInputHeight}
                {...rest}
              />
            ) : (
              <Input
                isShowError={isShowError}
                errorMessage={errorMessage}
                showsearchicon={showsearchicon}
                size={inputSize}
                placeholder={placeholder}
                id={name}
                name={name}
                type={type}
                ref={ref}
                className={
                  noPadding
                    ? cn(
                        'px-1 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                        shadow && 'focus:shadow',
                        variantClasses[variant],
                        sizeClasses[dimension],
                        disabled && 'bg-gray-100 cursor-not-allowed',
                        inputClassName,
                      )
                    : cn(
                        'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                        shadow && 'focus:shadow',
                        variantClasses[variant],
                        sizeClasses[dimension],
                        disabled && 'bg-gray-100 cursor-not-allowed',
                        inputClassName,
                      )
                }
                disabled={disabled}
                value={normalizedValue}
                spellCheck="false"
                onChange={onChange}
                aria-invalid={error ? 'true' : 'false'}
                rest={rest}
              />
            )}
          </React.Fragment>

          {error && (
            <InputError className="my-2 text-xs text-red-500">
              {error}
            </InputError>
          )}
        </PixelInputContainer>
      </DcDiv>
    ) : (
      <PixelInputContainer
        invalid={invalid}
        className={'overFlowCustom'}
        style={parentStyle}
        width={width}
      >
        <React.Fragment>
          {as === 'datePicker' ? (
            <React.Fragment>
              <DCDatepicker
                ref={ref}
                value={normalizedValue}
                className={cn(
                  ' px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                  shadow && 'focus:shadow',
                  variantClasses[variant],
                  sizeClasses[dimension],
                  disabled && 'bg-gray-100 cursor-not-allowed',
                  inputClassName,
                )}
                disabled={disabled}
                aria-invalid={error ? 'true' : 'false'}
                rest={rest}
                showResetDate={false}
                showClose={false}
                label={label}
                placeholder={placeholder}
                inputId={name}
                onChange={onChange}
                onDateChange={(data) =>
                  onChange(
                    { target: { value: data.date } },
                    setShowDatePicker(false),
                  )
                }
                onFocusChange={(focusedInput) =>
                  setShowDatePicker(focusedInput)
                }
                showDatepicker={showDatePicker}
              />
            </React.Fragment>
          ) : as === 'typeahead' ? (
            <TypeAHead
              onSelectedOption={onSelectedOption}
              isClearOnSelection={isClearOnSelection}
              customLabel={customLabel}
              isShowLabel={isShowLabel}
              placeholder={placeholder}
              id={name}
              name={name}
              type={type}
              ref={ref}
              labelKey={labelKey}
              className={cn(
                'flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                shadow && 'focus:shadow',
                variantClasses[variant],
                sizeClasses[dimension],
                disabled && 'bg-gray-100 cursor-not-allowed',
                inputClassName,
              )}
              disabled={disabled}
              value={normalizedValue}
              spellCheck="false"
              onChange={onChange}
              invalid={invalid}
              aria-invalid={error ? 'true' : 'false'}
              isShowIcon={isShowIcon}
              icon={icon}
              iconPosition={iconPosition}
              iconClick={iconClick}
              {...rest}
            />
          ) : as === 'password' ? (
            <PasswordInput
              value={normalizedValue}
              onChange={onChange}
              placeholder={placeholder}
              id={name}
              name={name}
              type={type}
              ref={ref}
              spellCheck="false"
              aria-invalid={error ? 'true' : 'false'}
              rest={rest}
              showsearchicon={showsearchicon}
              className={className}
              disabled={disabled}
            />
          ) : as === 'textarea' ? (
            <TextareaInput
              value={normalizedValue}
              onChange={onChange}
              placeholder={placeholder}
              id={name}
              name={name}
              ref={ref}
              variant={variant}
              dimension={dimension}
              disabled={disabled}
              error={error}
              isShowError={isShowError}
              errorMessage={errorMessage}
            />
          ) : as === 'otp' ? (
            <OtpInput
              length={otpLength}
              value={normalizedValue}
              onChange={onChange}
              onComplete={onOtpComplete}
              placeholder={placeholder}
              className={inputClassName}
              disabled={disabled}
              autoFocus={rest.autoFocus}
              size={inputSize}
              variant={variant}
              color={rest.color || 'primary'}
              isShowError={isShowError}
              errorMessage={errorMessage}
              label={label}
              isShowLabel={isShowLabel}
              isRequired={rest.required}
              helperText={rest.helperText}
              isShowHelperText={rest.isShowHelperText}
              isShowErrorMessage={rest.isShowErrorMessage}
              fullWidth={rest.fullWidth}
              gap={otpGap}
              inputWidth={otpInputWidth}
              inputHeight={otpInputHeight}
              {...rest}
            />
          ) : (
            <Input
              isShowError={isShowError}
              errorMessage={errorMessage}
              showsearchicon={showsearchicon}
              size={inputSize}
              placeholder={placeholder}
              id={name}
              name={name}
              type={type}
              ref={ref}
              className={
                noPadding
                  ? cn(
                      'px-1 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                      shadow && 'focus:shadow',
                      variantClasses[variant],
                      sizeClasses[dimension],
                      disabled && 'bg-gray-100 cursor-not-allowed',
                      inputClassName,
                    )
                  : cn(
                      'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                      shadow && 'focus:shadow',
                      variantClasses[variant],
                      sizeClasses[dimension],
                      disabled && 'bg-gray-100 cursor-not-allowed',
                      inputClassName,
                    )
              }
              disabled={disabled}
              value={normalizedValue}
              spellCheck="false"
              onChange={onChange}
              aria-invalid={error ? 'true' : 'false'}
              isShowIcon={isShowIcon}
              icon={icon}
              iconPosition={iconPosition}
              iconClick={iconClick}
              rest={rest}
            />
          )}
        </React.Fragment>

        {error && (
          <InputError className="my-2 text-xs text-red-500">{error}</InputError>
        )}
      </PixelInputContainer>
    )
  },
)

export default DcInput
